import { Box, VStack, Grid } from '@chakra-ui/react';
import React, { FC, useMemo, useState } from 'react';

import { TransactionResultModal } from '@app/components/TransactionResultModal';
import { useAddLiquidityPageData } from '@app/screens/liquidity/AddLiquidityV3/hooks';
import { Token } from '@app/types/token';
import { useMintState } from '@app/state/mintStore';
import { Bound } from '@cryptoalgebra/kim-sdk';
import LiquidityChart from '@app/screens/liquidity/AddLiquidityV3/components/LiquidityChart';
import { ModeSelector } from '@app/screens/liquidity/AddLiquidityV3/components/ModeSelector';
import { ProviderSelector } from '@app/screens/liquidity/AddLiquidityV3/components/ProviderSelector';
import { ProviderStrategyDetails } from '@app/screens/liquidity/AddLiquidityV3/components/ProviderStrategyDetails';
import { AddLiquidityTitle } from '@app/screens/liquidity/AddLiquidityV3/components/AddLiquidityTitle';
import { AddLiquidityTokensSelector } from '@app/screens/liquidity/AddLiquidityV3/components/AddLiquidityTokensSelector';
import { AddLiquidityByProvider } from '@app/screens/liquidity/AddLiquidityV3/components/AddLiquidityByProvider';
import { IchiPositionsDetails } from '@app/screens/liquidity/AddLiquidityV3/components/ichi/IchiPositionsDetails';

interface Props {
  inModalView?: boolean;
  initialData?: {
    from: {
      token: Token;
      amount: string;
    };
    to: {
      token: Token;
      amount: string;
    };
  };
}

export const AddLiquidityV3: FC<Props> = ({ initialData }) => {
  const [feedback, setFeedback] = useState<{
    status: 'success' | 'error';
    title: string;
    message: string;
  } | null>(null);

  const {
    state,
    handleFromTokenSelect,
    handleToTokenSelect,
    mintInfo,
    currencyA,
    currencyB,
    poolAddress,
    handleFlip,
    mintInfoWithProvider
  } = useAddLiquidityPageData(initialData);

  const { mode, strategy, provider } = useMintState();

  const { [Bound.LOWER]: priceLower, [Bound.UPPER]: priceUpper } =
    mintInfo.pricesAtTicks;

  const price = useMemo(() => {
    if (!mintInfo.price) return;

    return mintInfo.invertPrice ? mintInfo.price.invert() : mintInfo.price;
  }, [mintInfo]);

  return (
    <Box mx="auto" w="100%">
      <AddLiquidityTitle />
      <Grid
        gridTemplateColumns={['1fr', '1fr', '1fr', '1fr 1.3fr']}
        p={0}
        w="100%"
        gap={6}
      >
        <VStack gap={4}>
          <AddLiquidityTokensSelector
            currencyA={currencyA}
            currencyB={currencyB}
            handleFromTokenSelect={handleFromTokenSelect}
            handleToTokenSelect={handleToTokenSelect}
            state={state}
            handleFlip={handleFlip}
          />

          <ModeSelector mintInfo={mintInfoWithProvider} />
          <ProviderSelector
            poolAddress={poolAddress}
            mintInfo={mintInfoWithProvider}
            currencyB={currencyB}
            currencyA={currencyA}
          />

          <AddLiquidityByProvider
            poolAddress={poolAddress}
            currencyA={currencyA}
            currencyB={currencyB}
          />

          <TransactionResultModal
            status={feedback?.status}
            title={feedback?.title}
            message={feedback?.message}
            isOpen={!!feedback}
            onClose={() => setFeedback(null)}
          />
        </VStack>
        <VStack width="100%" alignItems="flex-start">
          {mode === 'manual' && (
            <LiquidityChart
              currencyA={mintInfo.invertPrice ? currencyB : currencyA}
              currencyB={mintInfo.invertPrice ? currencyA : currencyB}
              currentPrice={
                price ? parseFloat(price.toSignificant()) : undefined
              }
              priceLower={priceLower}
              priceUpper={priceUpper}
              poolState={mintInfo.poolState}
            />
          )}
          {mode === 'auto' &&
            provider === 'steer' &&
            poolAddress &&
            currencyA &&
            currencyB && (
              <ProviderStrategyDetails
                poolAddress={poolAddress}
                currencyA={currencyA}
                currencyB={currencyB}
                selectedVault={mintInfoWithProvider.selectedVault}
                provider={provider}
                hasStrategy={!!strategy}
                nftPoolAddress={mintInfoWithProvider.nftPoolAddress}
              />
            )}
          {mode === 'auto' &&
            provider === 'ichi' &&
            poolAddress &&
            currencyA &&
            currencyB && (
              <IchiPositionsDetails
                poolAddress={poolAddress}
                currencyA={currencyA}
                currencyB={currencyB}
              />
            )}
        </VStack>
      </Grid>
    </Box>
  );
};
